import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Slider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { PlayArrow, Pause, Save, Cancel, PlayCircleOutline } from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
// Temporarily comment out Regions plugin
// import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.js'; // Re-enable Regions plugin
// import { WaveSurferEvents } from 'wavesurfer.js'; // Add WaveSurferEvents import
// Add Minimap plugin import
import Minimap from 'wavesurfer.js/dist/plugins/minimap.js';

interface WaveformEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (key: string, inPoint: number, outPoint: number, gain: number) => void;
  audioUrl: string | null;
  initialInPoint: number;
  initialOutPoint: number;
  initialGain?: number; // New prop for initial gain setting
  lineKey: string;
  songTitle?: string;
  songArtist?: string;
  peaks?: number[];
  onPeaksGenerated?: (peaks: number[]) => void;
}

export const WaveformEditorDialog = React.memo<WaveformEditorDialogProps>((props) => {
  const {
    open,
    onClose,
    onSave,
    audioUrl,
    initialInPoint,
    initialOutPoint,
    initialGain = 0, // Default to 0dB if not provided
    lineKey,
    songTitle,
    songArtist,
    peaks,
    onPeaksGenerated,
  } = props;

  // *** 1. Log Props ***
  console.log('[WaveformEditor DEBUG] Props received:', { open, audioUrl, initialInPoint, initialOutPoint, initialGain, lineKey, peaks });
  // Log received peaks prop length specifically
  console.log(`[WaveformEditor DEBUG] Received peaks prop length: ${peaks?.length ?? 'undefined'}`);

  const [waveformContainer, setWaveformContainer] = useState<HTMLDivElement | null>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);
  // const wsRegions = useRef<RegionsPlugin | null>(null); // Commented out
  // const activeRegionRef = useRef<Region | null>(null); // Commented out
  const wsRegions = useRef<RegionsPlugin | null>(null); // Re-enable
  const activeRegionRef = useRef<Region | null>(null); // Re-enable

  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Re-enable region state
  const [currentInPoint, setCurrentInPoint] = useState(initialInPoint);
  const [currentOutPoint, setCurrentOutPoint] = useState(initialOutPoint);
  const [currentGain, setCurrentGain] = useState(initialGain); // New state for gain
  const [isPlayingRegion, setIsPlayingRegion] = useState(false); 
  // Add state for zoom level
  const [minPxPerSec, setMinPxPerSec] = useState<number>(100); // Default zoom level

  // Re-enable region update handler
  const handleRegionUpdateEnd = useCallback((region: Region) => {
    console.log('[WaveformEditor] Region update end:', region.start, region.end);
    setCurrentInPoint(region.start);
    setCurrentOutPoint(region.end);
    activeRegionRef.current = region; // Keep track of the active region
  }, []);

  // --- Define Event Handlers using useCallback ---
  const handleReady = useCallback(() => {
    // *** 5. Log Ready Event ***
    console.log('[WaveformEditor DEBUG] handleReady triggered');
    if (!wavesurfer.current || !wsRegions.current) { 
        console.error('[WaveformEditor DEBUG] handleReady: WaveSurfer or Regions plugin not ready!');
        setIsLoading(false);
        setError('Failed to initialize waveform regions.');
        return;
    }
    console.log(`[WaveformEditor] WaveSurfer 'ready' event fired. wsRegions available: ${!!wsRegions.current}`);
    const dur = wavesurfer.current.getDuration();
    setDuration(dur);
    setIsLoading(false);

    // --- Calculate and Apply Initial Zoom ---
    if (waveformContainer) {
        const containerWidth = waveformContainer.offsetWidth;
        const regionDuration = initialOutPoint - initialInPoint;
        if (containerWidth > 0 && regionDuration > 0.1) { 
            // Calculate zoom to show a good portion of the region
            // Using a smaller factor to show more context
            const visibleDuration = regionDuration * 2; // Show 2x the region duration
            let calculatedZoom = containerWidth / visibleDuration;
            calculatedZoom = Math.max(10, Math.min(calculatedZoom, 500));
            console.log(`[WaveformEditor DEBUG] handleReady Zoom Calculation: containerWidth=${containerWidth}, regionDuration=${regionDuration.toFixed(2)}, visibleDuration=${visibleDuration.toFixed(2)}, calculatedZoom=${calculatedZoom.toFixed(2)}`);
            setMinPxPerSec(calculatedZoom);
            wavesurfer.current.zoom(calculatedZoom);
            
            // Position playhead at IN point
            const inPoint = initialInPoint;
            const duration = wavesurfer.current.getDuration();
            if (duration > 0) {
                // Calculate the position in pixels of the IN point
                const inPointPosition = inPoint * calculatedZoom;
                // Position to put IN point 20% from left edge
                const desiredLeftMargin = containerWidth * 0.2;
                
                // Set the scroll position to place IN point at our desired left margin
                // We need to scroll just enough so the IN point appears 20% from left
                console.log(`[WaveformEditor DEBUG] Positioning view: inPointPosition=${inPointPosition.toFixed(0)}px, desiredLeftMargin=${desiredLeftMargin.toFixed(0)}px`);
                
                // Apply the zoom first
                wavesurfer.current.zoom(calculatedZoom);
                
                // Then seek to the IN point to set the playhead
                const inPointProgress = inPoint / duration;
                wavesurfer.current.seekTo(inPointProgress);
                setCurrentTime(inPoint);
                
                // Finally, adjust the scroll position using scrollLeft
                // Added timeout to ensure the seekTo and zoom have completed
                setTimeout(() => {
                    if (wavesurfer.current && wavesurfer.current.getWrapper()) {
                        try {
                            const scrollPosition = Math.max(0, inPointPosition - desiredLeftMargin);
                            console.log(`[WaveformEditor DEBUG] Setting scrollLeft to ${scrollPosition.toFixed(0)}px`);
                            
                            // Access the scroll container and set the scroll position directly
                            const scrollElement = wavesurfer.current.getWrapper().parentNode;
                            if (scrollElement && 'scrollLeft' in scrollElement) {
                                scrollElement.scrollLeft = scrollPosition;
                                console.log('[WaveformEditor DEBUG] Scroll position set successfully');
                            } else {
                                console.warn('[WaveformEditor DEBUG] Could not find scrollable parent element');
                            }
                        } catch (e) {
                            console.error('[WaveformEditor DEBUG] Error setting scroll position:', e);
                        }
                    }
                }, 100);
            } else {
                console.log(`[WaveformEditor DEBUG] Cannot calculate IN point progress, duration is 0.`);
            }
        } else {
             console.log('[WaveformEditor DEBUG] Skipping initial zoom calculation (container width or region duration too small).');
             wavesurfer.current.zoom(minPxPerSec); // Apply default zoom if calculation skipped
             
             // Simple IN point positioning for default zoom
             const duration = wavesurfer.current.getDuration();
             if (duration > 0) {
                const inPointProgress = initialInPoint / duration;
                console.log(`[WaveformEditor DEBUG] Seeking to IN point: ${inPointProgress.toFixed(3)} (time: ${initialInPoint.toFixed(2)}s)`);
                wavesurfer.current.seekTo(inPointProgress);
                setCurrentTime(initialInPoint);
             } else {
                 console.log(`[WaveformEditor DEBUG] Cannot calculate IN point progress, duration is 0.`);
             }
        }
    } else {
        console.warn('[WaveformEditor DEBUG] waveformContainer not available in handleReady for zoom calculation.');
        wavesurfer.current.zoom(minPxPerSec); // Apply default zoom if container not ready
        
        // Simple positioning if container isn't ready
        const duration = wavesurfer.current.getDuration();
        if (duration > 0) {
            const inPointProgress = initialInPoint / duration;
            console.log(`[WaveformEditor DEBUG] Seeking to IN point: ${inPointProgress.toFixed(3)} (time: ${initialInPoint.toFixed(2)}s)`);
            wavesurfer.current.seekTo(inPointProgress);
            setCurrentTime(initialInPoint);
        } else {
            console.log(`[WaveformEditor DEBUG] Cannot calculate IN point progress, duration is 0.`);
        }
    }
    // ----------------------------------------

    // Re-enable region creation
    
    // Clear any previous regions and add the initial one
    console.log('[WaveformEditor DEBUG] handleReady: Clearing previous regions.');
    wsRegions.current.clearRegions();
    
    const regionParams = {
      start: Math.max(0, initialInPoint), // Ensure start isn't negative
      end: Math.min(dur, initialOutPoint), // Ensure end doesn't exceed duration
      color: 'rgba(255, 0, 0, 0.1)', // Example color
      drag: true,
      resize: true,
    };
    console.log('[WaveformEditor DEBUG] handleReady: Attempting to add region with params:', regionParams);
    try {
      const region = wsRegions.current.addRegion(regionParams);
      console.log('[WaveformEditor DEBUG] handleReady: Region added successfully:', region);
      activeRegionRef.current = region; // Store ref to the created region
    } catch (regionError) {
      console.error('[WaveformEditor DEBUG] handleReady: Error adding region:', regionError);
      setError('Failed to create initial editing region.');
      activeRegionRef.current = null; // Ensure ref is null on error
    }

    // Attach listener for region updates
    // Use 'region-update-end' to only capture final position after dragging
    if (wsRegions.current && activeRegionRef.current) { // Check if region was added before attaching listener
        console.log('[WaveformEditor DEBUG] handleReady: Attaching region-updated listener.');
        wsRegions.current.on('region-updated', handleRegionUpdateEnd);
    } else {
        console.warn('[WaveformEditor DEBUG] handleReady: Skipping region-updated listener attachment due to missing region.');
    }
    
  }, [initialInPoint, initialOutPoint, handleRegionUpdateEnd, peaks, onPeaksGenerated, waveformContainer]);

  const handleError = useCallback((err: string | Error) => {
    const errorMsg = typeof err === 'string' ? err : err.message;
    console.error(`[WaveformEditor] WaveSurfer 'error' event fired:`, errorMsg);
    setError(`Failed to load audio: ${errorMsg}`);
    setIsLoading(false);
  }, []);
  
  const handleAudioprocess = useCallback((time: number) => {
    setCurrentTime(time); // Update time first

    if (isPlayingRegion && activeRegionRef.current && wavesurfer.current) {
      const regionEndTime = activeRegionRef.current.end;
      // Use a slightly bigger margin to ensure we don't miss the end point
      const epsilon = 0.05; // 50ms margin
      
      // Check if we've reached or passed the end of the region
      const shouldStop = time >= regionEndTime - epsilon;

      // Only log when approaching the end to reduce console spam
      if (time > regionEndTime - 0.5) {
        console.log(`[WaveformEditor DEBUG] handleAudioprocess: time=${time.toFixed(3)}, regionEnd=${regionEndTime.toFixed(3)}, shouldStop=${shouldStop}`);
      }

      if (shouldStop) {
        console.log(`[WaveformEditor DEBUG] Region end detected at time=${time.toFixed(3)}. Pausing.`);
        wavesurfer.current.pause(); // This should trigger handlePause
        // In some cases, pause event might not fire so we reset the state manually
        setIsPlaying(false);
        setIsPlayingRegion(false);
        
        // Explicit seek to region end to ensure visual indication is correct
        const seekPosition = regionEndTime / wavesurfer.current.getDuration();
        wavesurfer.current.seekTo(seekPosition);
      }
    }
  }, [isPlayingRegion]); // Keep dependency only on isPlayingRegion

  const handleSeeking = useCallback((time: number) => setCurrentTime(time), []);
  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => {
    console.log("[WaveformEditor DEBUG] handlePause triggered. Setting isPlaying=false, isPlayingRegion=false.");
    setIsPlaying(false);
    setIsPlayingRegion(false); // Reset flag on any pause
  // No dependencies needed here as it only calls setters
  }, []);
  const handleFinish = useCallback(() => {
    console.log('[WaveformEditor] handleFinish triggered');
    setIsPlaying(false);
  }, []);

  // Add effect to sync internal gain state with initialGain prop
  useEffect(() => {
    console.log(`[WaveformEditor Effect] initialGain prop is now: ${initialGain}. Syncing currentGain state.`);
    setCurrentGain(initialGain);
  }, [initialGain]); // Run when initialGain prop changes

  // Main Initialization Effect
  useEffect(() => {
    console.log('[WaveformEditor] useEffect triggered. Open:', open, 'Audio URL:', audioUrl, 'Peaks provided:', !!peaks);
    if (!open || !waveformContainer) return;

    setIsLoading(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    if (wavesurfer.current) {
      console.log('[WaveformEditor] Destroying previous WaveSurfer instance.');
      wavesurfer.current.destroy();
      wavesurfer.current = null;
      wsRegions.current = null; // Also clear regions ref
      activeRegionRef.current = null;
    }

    console.log(`[WaveformEditor] Initializing WaveSurfer...`);
    let wsInstance: WaveSurfer | null = null;
    try {
        const options = {
            container: waveformContainer,
            waveColor: 'rgb(200, 0, 200)',
            progressColor: 'rgb(100, 0, 100)',
            height: 100,
            normalize: !peaks,
            peaks: peaks ? [peaks] : undefined,
        };
        console.log('[WaveformEditor DEBUG] WaveSurfer create options:', options);
        wsInstance = WaveSurfer.create(options);
        wavesurfer.current = wsInstance;
        // wsRegions.current = wsInstance.plugins[0] as RegionsPlugin; // Store reference to Regions plugin
        // Re-enable plugin registration
        wsRegions.current = wsInstance.registerPlugin(RegionsPlugin.create());
        // Register Minimap plugin with default options
        console.log('[WaveformEditor DEBUG] Registering Minimap plugin.');
        wsInstance.registerPlugin(Minimap.create({
            // Optional: customize Minimap appearance if needed
            // height: 30,
            // waveColor: '#ddd',
            // progressColor: '#999',
        }));

        // Load audio separately if URL exists
        if (audioUrl) {
          console.log(`[WaveformEditor] Loading audio URL: ${audioUrl}`);
          wsInstance.load(audioUrl);
        } else {
          console.error('[WaveformEditor] No audioUrl provided, cannot load audio.');
          setError('No audio URL available');
          setIsLoading(false);
          // Potentially return early or handle this state
        }

        // Attach listeners
        wsInstance.once('ready', handleReady);
        wsInstance.once('error', handleError);
        wsInstance.on('audioprocess', handleAudioprocess);
        wsInstance.on('seeking', handleSeeking);
        wsInstance.on('play', handlePlay);
        wsInstance.on('pause', handlePause);
        wsInstance.on('finish', handleFinish);

    } catch (initError) {
        console.error('[WaveformEditor] Error initializing WaveSurfer:', initError);
        setError(`Initialization failed: ${initError instanceof Error ? initError.message : String(initError)}`);
        setIsLoading(false);
    }

    // Cleanup function
    return () => {
        if (wsInstance) {
             console.log("[WaveformEditor] Cleaning up listeners and destroying instance");
             // Use the local variable wsInstance for cleanup
             // Re-enable region plugin cleanup
             if (wsRegions.current) {
                 wsRegions.current.un('region-updated', handleRegionUpdateEnd);
             }
             wsInstance.un('ready', handleReady);
             wsInstance.un('error', handleError);
             wsInstance.un('audioprocess', handleAudioprocess);
             wsInstance.un('seeking', handleSeeking);
             wsInstance.un('play', handlePlay);
             wsInstance.un('pause', handlePause);
             wsInstance.un('finish', handleFinish);
             wsInstance.destroy();
        }
    };
  }, [open, audioUrl, initialInPoint, initialOutPoint, handleReady, handleError, handleAudioprocess, handleSeeking, handlePlay, handlePause, handleFinish, handleRegionUpdateEnd, peaks, waveformContainer]);

  // Apply gain to audio on play
  useEffect(() => {
    if (wavesurfer.current && open) {
      // Get the media element
      // Convert dB to linear gain (0.0 to 1.0 range)
      const linearGain = Math.pow(10, currentGain / 20);
      const volumeLevel = Math.min(1.0, Math.max(0.0, linearGain)); // Ensure value is between 0 and 1
      
      // Use wavesurfer's setVolume method
      try {
        wavesurfer.current.setVolume(volumeLevel);
        console.log(`[WaveformEditor DEBUG] Applied gain via setVolume: ${currentGain}dB (linear: ${linearGain.toFixed(3)}, setVolume: ${volumeLevel.toFixed(3)})`);
      } catch (error) {
        console.error("[WaveformEditor DEBUG] Error setting volume via wavesurfer:", error);
      }
    }
  }, [currentGain, wavesurfer, open]);

  // Helper function to apply volume just before play
  const applyVolume = useCallback(() => {
    if (wavesurfer.current) {
      const linearGain = Math.pow(10, currentGain / 20);
      const volumeLevel = Math.min(1.0, Math.max(0.0, linearGain));
      try {
        wavesurfer.current.setVolume(volumeLevel);
        console.log(`[WaveformEditor DEBUG] Applying volume (${volumeLevel.toFixed(3)}) just before play.`);
      } catch (error) {
        console.error("[WaveformEditor DEBUG] Error setting volume before play:", error);
      }
    }
  }, [currentGain]); // Depends on currentGain

  const handlePlayPause = () => {
    console.log('[WaveformEditor DEBUG] handlePlayPause called. isPlaying:', isPlaying);
    
    // If already playing, just pause
    if (isPlaying) {
      console.log('[WaveformEditor DEBUG] handlePlayPause: Already playing, pausing now.');
      if (wavesurfer.current) {
        wavesurfer.current.pause();
      }
      return;
    }
    
    // Otherwise, play from the region IN point
    if (activeRegionRef.current && wavesurfer.current) {
      console.log('[WaveformEditor DEBUG] handlePlayPause: Playing from region IN point.');
      const ws = wavesurfer.current;
      const region = activeRegionRef.current;
      const duration = ws.getDuration();
      
      if (duration > 0) {
        // Always start from the region's IN point
        const inPoint = region.start;
        const seekTarget = inPoint / duration;
        
        // Mark as playing region BEFORE we start the process
        console.log('[WaveformEditor DEBUG] handlePlayPause: Setting isPlayingRegion = true.');
        setIsPlayingRegion(true);
        
        console.log(`[WaveformEditor DEBUG] handlePlayPause: Seeking to IN point ${inPoint.toFixed(3)}s (${seekTarget.toFixed(3)})`);
        ws.seekTo(seekTarget);
        
        // Increase timeout to 150ms to ensure seek completes on all devices
        setTimeout(() => {
          if (!wavesurfer.current) {
            console.log('[WaveformEditor DEBUG] handlePlayPause: Timeout fired but wavesurfer no longer exists');
            setIsPlayingRegion(false);
            return;
          }
          
          try {
            console.log('[WaveformEditor DEBUG] handlePlayPause: Timeout fired, applying volume and playing now from IN point');
            applyVolume(); // Apply volume right before playing
            wavesurfer.current.play();
            // Update UI state AFTER successful play command
            setIsPlaying(true);
            
            // Force update current time to show correct position
            setCurrentTime(inPoint);
          } catch (e) {
            console.error('[WaveformEditor DEBUG] handlePlayPause: Error starting playback:', e);
            setIsPlayingRegion(false);
            setIsPlaying(false);
          }
        }, 150);
      } else {
        console.warn('[WaveformEditor DEBUG] handlePlayPause: Attempted to play, but duration is not available yet.');
      }
    } else {
      // Fallback to normal play/pause if no region
      console.log('[WaveformEditor DEBUG] handlePlayPause: No region available, using normal playback.');
      wavesurfer.current?.playPause();
    }
  };

  // Add handler to play only the selected region
  const handlePlayRegion = () => {
    // Re-enable this function
    // console.warn('[WaveformEditor DEBUG] handlePlayRegion called, but region functionality is temporarily disabled.')
    
    console.log(`[WaveformEditor DEBUG] handlePlayRegion called. activeRegionRef.current: ${!!activeRegionRef.current}, wavesurfer.current: ${!!wavesurfer.current}`);
    if (activeRegionRef.current && wavesurfer.current) {
        const ws = wavesurfer.current;
        const region = activeRegionRef.current;
        const duration = ws.getDuration();
        
        console.log(`[WaveformEditor DEBUG] handlePlayRegion: Duration: ${duration}`);
        if (duration > 0) { // Ensure duration is loaded
            // Ensure paused state before starting region play
            if (ws.isPlaying()) {
                console.log('[WaveformEditor DEBUG] handlePlayRegion: Pausing before seeking.');
                ws.pause();
            }
            setIsPlayingRegion(true); // Set the flag
            // Seek to the start of the region before playing
            const seekTarget = region.start / duration;
            console.log(`[WaveformEditor DEBUG] handlePlayRegion: Seeking to ${seekTarget.toFixed(3)} (start: ${region.start.toFixed(3)})`);
            ws.seekTo(seekTarget); 

            // Add a one-time listener for the 'seeking' event
            const playOnSeek = () => {
                console.log(`[WaveformEditor DEBUG] Seek complete. Applying volume and playing region from ${region.start.toFixed(2)}.`);
                if (wavesurfer.current && isPlayingRegion) { // Double check state hasn't changed
                    console.log('[WaveformEditor DEBUG] playOnSeek: Calling wavesurfer.play().');
                    applyVolume(); // Apply volume right before playing
                    wavesurfer.current.play();
                } else {
                    console.log(`[WaveformEditor DEBUG] playOnSeek: Condition not met, not playing (ws.current: ${!!wavesurfer.current}, isPlayingRegion: ${isPlayingRegion})`);
                }
                // Remove the listener to avoid it firing again
                console.log('[WaveformEditor DEBUG] playOnSeek: Removing \'seeking\' listener.');
                ws.un('seeking', playOnSeek);
            };
            console.log('[WaveformEditor DEBUG] handlePlayRegion: Attaching \'seeking\' listener.');
            ws.once('seeking', playOnSeek);

        } else {
             console.warn('[WaveformEditor DEBUG] handlePlayRegion: Attempted to play region, but duration is not available yet.');
        }

    } else {
      console.warn('[WaveformEditor DEBUG] handlePlayRegion: Attempted to play region, but no active region or wavesurfer found.');
    }
    
  };

  const handleSave = () => {
    onSave(lineKey, currentInPoint, currentOutPoint, currentGain);
    onClose();
  };
  
  const formatTime = (seconds: number): string => {
      const date = new Date(0);
      date.setSeconds(seconds || 0);
      return date.toISOString().substring(14, 19);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {
          (lineKey === 'Intro' || lineKey === 'Outro') 
            ? lineKey 
            : (songTitle && songArtist) 
              ? `${lineKey} - ${songTitle} by ${songArtist}` 
              : lineKey // Fallback to just lineKey if title/artist missing for numbered lines
        }
      </DialogTitle>
      <DialogContent>
        <Box ref={setWaveformContainer} sx={{ width: '100%', minHeight: '100px', mb: 2, position: 'relative', border: '1px solid #ccc', background: '#f0f0f0' }}>
            {isLoading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.8)' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 1 }}>Loading audio...</Typography>
                </Box>
            )}
            {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
        </Box>

        {/* Move Zoom Slider here */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, px: 1 }}>
            <Typography variant="caption" sx={{ minWidth: '40px' }}>Zoom:</Typography>
            {/* Log the state value right before rendering the slider (valid JSX way) */}
            {(() => { 
                console.log(`[WaveformEditor DEBUG] Rendering Zoom Slider with minPxPerSec: ${minPxPerSec}`); 
                return null; 
            })()}
            <Slider
                size="small"
                value={minPxPerSec}
                min={10}  // Minimum zoom level (most zoomed out)
                max={500} // Maximum zoom level (most zoomed in)
                step={10}
                // Wrap onChange logic in useCallback
                onChange={useCallback((event: Event, value: number | number[]) => {
                    // Handle potential array value if slider has multiple thumbs (though unlikely here)
                    const newZoom = typeof value === 'number' ? value : value[0]; 
                    console.log(`[WaveformEditor DEBUG] Zoom slider onChange fired! New value: ${newZoom}`);
                    setMinPxPerSec(newZoom); // Use state setter from component scope
                    if (wavesurfer.current) {
                        console.log(`[WaveformEditor DEBUG] Calling wavesurfer.zoom(${newZoom})`);
                        wavesurfer.current.zoom(newZoom);
                    } else {
                        console.warn('[WaveformEditor DEBUG] Cannot call zoom, wavesurfer.current is null.');
                    }
                }, [setMinPxPerSec])} // Dependency on the state setter function
                // Add onMouseDown for basic click detection
                onMouseDown={() => {
                    console.log('[WaveformEditor DEBUG] Zoom slider MouseDown event fired!');
                }}
                disabled={isLoading || !!error}
                sx={{ flexGrow: 1 }}
                valueLabelDisplay="auto"
            />
        </Box>
        
        {/* Add Gain Control */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, px: 1 }}>
            <Typography variant="caption" sx={{ minWidth: '40px' }}>Gain:</Typography>
            {/* Add log right before rendering the Slider */}
            {(() => {
                console.log(`[WaveformEditor Render] Rendering Gain Slider with currentGain value: ${currentGain}`);
                return null; // Render nothing
            })()}
            <Slider
                size="small"
                value={currentGain}
                min={-24}  // Changed from -12
                max={24}   // Changed from 12
                step={0.5}
                onChange={(event: Event, value: number | number[]) => {
                    const newGain = typeof value === 'number' ? value : value[0];
                    console.log(`[WaveformEditor DEBUG] Gain slider changed to: ${newGain}dB`);
                    setCurrentGain(newGain);
                }}
                disabled={isLoading || !!error}
                sx={{ flexGrow: 1 }}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value > 0 ? '+' : ''}${value}dB`}
                marks={[
                  { value: -24, label: '-24dB' }, // Changed from -12
                  { value: 0, label: '0dB' },
                  { value: 24, label: '+24dB' }, // Changed from 12
                ]}
            />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={handlePlayPause} disabled={isLoading || !!error} color="primary" size="large">
                {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Typography variant="caption">{formatTime(currentTime)}</Typography>
            <Slider
                size="small"
                value={currentTime}
                max={duration}
                onChange={(_, value) => wavesurfer.current?.seekTo((value as number) / duration)}
                disabled={isLoading || !!error}
                sx={{ flexGrow: 1 }}
                step={0.01}
                valueLabelDisplay="auto"
                valueLabelFormat={formatTime}
            />
            <Typography variant="caption">{formatTime(duration)}</Typography>
        </Box>
        
        {/* Re-enable current points display */}
        
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', pt: 1 }}>
            <Typography variant="body2">Current In: <strong>{currentInPoint.toFixed(2)}s</strong></Typography>
            <Typography variant="body2">Current Out: <strong>{currentOutPoint.toFixed(2)}s</strong></Typography>
            <Typography variant="body2">Gain: <strong>{currentGain > 0 ? '+' : ''}{currentGain.toFixed(1)}dB</strong></Typography>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Cancel />}>Cancel</Button>
        <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<Save />} 
            disabled={isLoading || !!error} 
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}); 